// src/features/rrhh/empleados/components/EmpleadosFilters.jsx
import { useMemo, useState } from "react";
import {
  Box,
  ClickAwayListener,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";

function FilterDropdown({
  label,
  value,
  options = [],
  onChange,
  variant = "neutral", // neutral | secondary
}) {
  const [open, setOpen] = useState(false);

  const buttonSx =
    variant === "secondary"
      ? {
          py: 0.75,
          px: 1.5,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.4,
          bgcolor: "secondary.500",
          borderRadius: "33px",
          boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
          transition: "all .18s ease",
          "&:hover": { bgcolor: "secondary.600" },
        }
      : {
          py: 0.75,
          px: 1.5,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.4,
          bgcolor: "neutral.100",
          borderRadius: "33px",
          boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
          transition: "all .18s ease",
          "&:hover": { bgcolor: "neutral.150" },
        };

  const textColor = variant === "secondary" ? "logoblanco" : "primary.900";
  const menuBg = variant === "secondary" ? "secondary.50" : "neutral.50";

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: "relative" }}>
        <Box
          component="button"
          onClick={() => setOpen((prev) => !prev)}
          style={{
            border: "none",
            background: "transparent",
            padding: 0,
            cursor: "pointer",
          }}
        >
          <Box sx={buttonSx}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: textColor,
                whiteSpace: "nowrap",
              }}
            >
              {label}: {value}
            </Typography>

            <ArrowDropDownIcon
              sx={{
                color: textColor,
                fontSize: 18,
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform .18s ease",
              }}
            />
          </Box>
        </Box>

        {open ? (
          <Box
            sx={{
              position: "absolute",
              top: "calc(100% + 8px)",
              left: 0,
              minWidth: 180,
              maxHeight: 260,
              overflowY: "auto",
              bgcolor: menuBg,
              borderRadius: "14px",
              boxShadow: "0 10px 25px rgba(0,0,0,.12)",
              zIndex: 30,
              py: 0.6,
            }}
          >
            {options.map((item) => {
              const active = item === value;

              return (
                <Box
                  key={item}
                  onClick={() => {
                    onChange?.(item);
                    setOpen(false);
                  }}
                  sx={{
                    px: 1.5,
                    py: 1,
                    cursor: "pointer",
                    bgcolor: active ? "rgba(216, 220, 237, 0.55)" : "transparent",
                    transition: "background .15s ease",
                    "&:hover": {
                      bgcolor:
                        variant === "secondary"
                          ? "secondary.100"
                          : "neutral.100",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: "primary.900",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ) : null}
      </Box>
    </ClickAwayListener>
  );
}

function SearchPill({ value, onChange }) {
  return (
    <Box
      sx={{
        width: { xs: "100%", md: 340 },
        "& .MuiOutlinedInput-root": {
          height: "38px",
          bgcolor: "neutral.50",
          borderRadius: "24px",
          boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
          pl: 1,
          pr: 0.5,
          "& fieldset": {
            border: "none",
          },
        },
        "& .MuiInputBase-input": {
          fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
          fontWeight: 600,
          fontSize: "13px",
          color: "primary.900",
          py: 0,
        },
        "& .MuiInputBase-input::placeholder": {
          color: "neutral.700",
          opacity: 0.9,
        },
      }}
    >
      <TextField
        fullWidth
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder="Buscar por nombre, DNI o fecha de alta..."
        InputProps={{
          endAdornment: (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
              {!!value && (
                <IconButton
                  size="small"
                  onClick={() => onChange?.("")}
                  sx={{ p: 0.2 }}
                >
                  <CloseIcon sx={{ fontSize: 18, color: "neutral.700" }} />
                </IconButton>
              )}
              <SearchIcon sx={{ color: "primary.900", fontSize: 22 }} />
            </Box>
          ),
        }}
      />
    </Box>
  );
}

export default function EmpleadosFilters({
  contrato = "Todos",
  situacion = "Todos",
  sucursal = "Todas",
  area = "Todas",
  rol = "Todos",
  search = "",
  contratoOptions = ["Todos", "Activo", "Inactivo"],
  situacionOptions = ["Todos", "Formal", "Informal"],
  sucursalOptions = ["Todas"],
  areaOptions = ["Todas"],
  rolOptions = ["Todos"],
  onChangeContrato,
  onChangeSituacion,
  onChangeSucursal,
  onChangeArea,
  onChangeRol,
  onChangeSearch,
}) {
  const orderedContratoOptions = useMemo(
    () => [...new Set(contratoOptions)],
    [contratoOptions]
  );

  const orderedSituacionOptions = useMemo(
    () => [...new Set(situacionOptions)],
    [situacionOptions]
  );

  const orderedSucursalOptions = useMemo(
    () => [...new Set(sucursalOptions)],
    [sucursalOptions]
  );

  const orderedAreaOptions = useMemo(
    () => [...new Set(areaOptions)],
    [areaOptions]
  );

  const orderedRolOptions = useMemo(
    () => [...new Set(rolOptions)],
    [rolOptions]
  );

  return (
    <Stack
      spacing={1.4}
      sx={{
        width: "100%",
      }}
    >
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={1.2}
        sx={{
          alignItems: { xs: "stretch", lg: "center" },
          justifyContent: "space-between",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          sx={{ alignItems: "center" }}
        >
          <FilterDropdown
            label="Contrato"
            value={contrato}
            options={orderedContratoOptions}
            onChange={onChangeContrato}
            variant="neutral"
          />

          <FilterDropdown
            label="Situación"
            value={situacion}
            options={orderedSituacionOptions}
            onChange={onChangeSituacion}
            variant="neutral"
          />

          <FilterDropdown
            label="Sucursal"
            value={sucursal}
            options={orderedSucursalOptions}
            onChange={onChangeSucursal}
            variant="neutral"
          />

          <FilterDropdown
            label="Área"
            value={area}
            options={orderedAreaOptions}
            onChange={onChangeArea}
            variant="neutral"
          />

          <FilterDropdown
            label="Rol"
            value={rol}
            options={orderedRolOptions}
            onChange={onChangeRol}
            variant="neutral"
          />
        </Stack>

        <SearchPill value={search} onChange={onChangeSearch} />
      </Stack>
    </Stack>
  );
}